import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import type { DetailDrawerTab } from './detail-drawer';

export interface DetailDrawerBodyProps {
  tabs: readonly DetailDrawerTab[];
  tab: string;
  onTabChange: (value: string) => void;
  loading: boolean;
}

const BODY_CLASS = 'min-h-0 flex-1 overflow-y-auto scrollbar-thin px-5 py-4';

const LoadingBody = () => (
  <div className="space-y-3">
    <Skeleton className="h-4 w-3/4" />
    <Skeleton className="h-4 w-1/2" />
    <Skeleton className="h-24 w-full" />
  </div>
);

/**
 * What the record panel shows below its header.
 *
 * A record with one section is not a tabbed record: a strip holding a single
 * tab is chrome that offers no choice, and it made the same panel read as a
 * different component from its siblings. Below two tabs the section is simply
 * the panel — and there is no tab list left pointing at a tab nobody can press.
 */
export function DetailDrawerBody({ tabs, tab, onTabChange, loading }: DetailDrawerBodyProps) {
  const single = tabs.length === 1 ? tabs[0] : undefined;

  if (single) {
    return <div className={BODY_CLASS}>{loading ? <LoadingBody /> : single.content}</div>;
  }

  return (
    <Tabs value={tab} onValueChange={onTabChange} className="flex min-h-0 flex-1 flex-col">
      {/* Five labels are wider than a 375px sheet: below `sm` the strip scrolls
          instead of clipping the last tab out of reach. The extra pixel of
          padding is what the trigger's `-mb-px` needs so its underline is not
          cut by the scroll container. */}
      <TabsList className="shrink-0 gap-3 px-5 max-sm:overflow-x-auto max-sm:overflow-y-hidden max-sm:pb-px sm:gap-4">
        {tabs.map((item) => (
          <TabsTrigger key={item.value} value={item.value}>
            {item.label}
            {item.count !== undefined && (
              <span className="ml-1.5 text-xs tabular text-muted-foreground">{item.count}</span>
            )}
          </TabsTrigger>
        ))}
      </TabsList>

      <div className={BODY_CLASS}>
        {loading ? (
          <LoadingBody />
        ) : (
          tabs.map((item) => (
            <TabsContent key={item.value} value={item.value} className="mt-0">
              {item.content}
            </TabsContent>
          ))
        )}
      </div>
    </Tabs>
  );
}
